/**
 * 제출 후 정답·해설 보기. 학생 본인의 제출 완료 시도에서 지점마다 "내가 고른 답 / 정답 전체 / 업로드 해설 / 원본 근거"를 펼쳐 본다.
 * 서버(GET /attempts/:id/result-detail)가 소유권·제출 여부·최초 채점 기준을 지킨다. 이 화면은 받은 값을 보여 줄 뿐 판단하지 않는다.
 * 원본에 없는 근거는 만들지 않고 '미등록'으로 표시한다. 이 해설은 '근거 문제 풀기'와 다르며 근거 통과로 세지 않는다.
 * 조회 실패는 빈 목록과 다르게 알리고 지난 표시를 숨긴다.
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';

export interface DetailPoint {
  pointId: string; displayNumber: string; state: 'pending' | 'graded'; result: 'correct' | 'incorrect' | null;
  selected: { id: string; label: string | null }[]; answered: boolean; correct: { id: string; label: string }[] | null;
  explanation: string | null; originalEvidence: string | null; evidenceRegistered: boolean;
  /** 'after_evidence': 최초 오답이라 근거 문제를 푼 뒤에 정답·해설을 보여 주는 서버 정책(채점 대기와 다르다) */
  disclosure?: 'open' | 'after_evidence';
}

export const joinLabels = (xs: readonly { label: string | null }[]): string => xs.map((x) => x.label ?? '').filter((x) => x !== '').join(', ');

export function detailRows(p: DetailPoint): { key: string; label: string; value: string }[] {
  const mine = !p.answered ? '고른 보기 없음(미응답)' : joinLabels(p.selected) || '고른 보기 없음(미응답)';
  const rows = [{ key: 'mine', label: '내가 고른 답', value: mine }];
  if (p.disclosure === 'after_evidence') return [...rows, { key: 'wait', label: '정답', value: '틀린 문제예요. "오답·근거 다시 풀기"에서 근거 문제를 푼 뒤에 정답과 해설을 볼 수 있어요' }];
  if (p.state === 'pending' || p.correct === null) return [...rows, { key: 'wait', label: '정답', value: '결과 대기 중이라 아직 보여 줄 수 없어요' }];
  return [...rows,
    { key: 'correct', label: '정답 전체', value: joinLabels(p.correct) },
    { key: 'explanation', label: '업로드 해설', value: p.explanation ?? '미등록' },
    { key: 'evidence', label: '원본 근거', value: p.originalEvidence ?? '미등록' }];
}

export function mountResultDetail(root: HTMLElement, t: Transport, attemptId: string, onBack: () => void): void {
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const msg = h('p', { role: 'status', 'data-testid': 'detail-msg' });
  const body = h('div', { 'data-testid': 'detail-body' });
  clear(root);
  root.append(h('div', { class: 'lhead' }, h('h2', {}, '정답·해설 보기'), h('button', { class: 'secondary', 'data-testid': 'detail-back', onclick: onBack }, '결과로 돌아가기')),
    h('p', {}, '내가 제출한 시도의 정답과 업로드된 해설이에요. 근거 문제 풀기와는 달라요.'), msg, body);

  async function load(): Promise<void> {
    msg.textContent = '불러오는 중…';
    const r = await t.request('GET', `/attempts/${attemptId}/result-detail`);
    if (!live()) return;
    clear(body);
    if (r.kind !== 'http' || r.status !== 200) {
      msg.textContent = '정답·해설을 불러오지 못했어요. 결과 대기가 아니라 받아 오지 못한 상태예요.';
      body.append(h('button', { class: 'primary', 'data-testid': 'detail-retry', onclick: () => void load() }, '다시 불러오기'));
      return;
    }
    if (r.body['submitted'] !== true) { msg.textContent = '제출한 뒤에 볼 수 있어요.'; return; }
    msg.textContent = '';
    const pts = r.body['points'] as DetailPoint[];
    // 해설은 지점마다 접혀 있어 처음에는 번호와 맞음·틀림만 보인다: 한 번에 펼칠 수 있게 한다(기본은 접힘 유지)
    if (pts.length > 0) {
      const toggle = h('button', { class: 'secondary', type: 'button', 'aria-expanded': 'false', 'data-testid': 'detail-expand-all' }, '모든 지점의 정답·해설 펼치기');
      toggle.onclick = () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        for (const d of Array.from(body.querySelectorAll('details'))) (d as HTMLDetailsElement).open = open;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = open ? '모두 접기' : '모든 지점의 정답·해설 펼치기';
      };
      body.append(h('p', {}, '지점 번호를 누르면 내가 고른 답, 정답 전체, 해설이 펼쳐져요.'), toggle);
    }
    for (const p of pts) {
      const mark = p.state === 'pending' ? '결과 대기' : p.result === 'correct' ? '맞음' : '틀림';
      body.append(h('details', { class: 'detail-point', 'data-testid': `detail-${p.pointId}`, 'data-r': p.result ?? 'pending' },
        h('summary', {}, `${p.displayNumber}번 · ${mark}`),
        h('dl', {}, ...detailRows(p).flatMap((row) => [h('dt', {}, row.label), h('dd', { 'data-testid': `detail-${row.key}-${p.pointId}` }, row.value)]))));
    }
  }
  void load();
}
